import { c as _c } from "react/compiler-runtime";
import type { Language, Locale, OnboardingSessionConfig } from '@onefootprint/request-types';
import { PageShell } from '@onefootprint/ui';
import { useTranslation } from 'react-i18next';
import type { DeviceInfo } from '@/idv2/utils/get-device';
import { getLogger } from '@/idv2/utils/logger';
import useCreateHandoffUrl from '../hooks/use-create-handoff-url';
import useD2pSession from '../hooks/use-d2p-session';
import useRequirementsTitle from '../hooks/use-requirements-title';
import type { TransferRequirements } from '../types';
import CopyOrSmsSendLink from './copy-or-sms-send-link';
import ProcessingOverlay from './processing-overlay';
const {
  logInfo
} = getLogger({
  name: 'idv2',
  location: 'sms-transfer'
});
type SmsTransferProps = {
  authToken: string;
  config: OnboardingSessionConfig;
  device: DeviceInfo;
  l10n: {
    locale?: Locale;
    language: Language;
  };
  missingRequirements: TransferRequirements;
  sandboxIdDocOutcome?: string;
  fromContinueOnMobile: boolean;
  onDone: () => void;
};
const SmsTransfer = t0 => {
  const $ = _c(35);
  const {
    authToken,
    config,
    device,
    l10n,
    missingRequirements,
    sandboxIdDocOutcome,
    fromContinueOnMobile,
    onDone
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "transfer.smsTransfer"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("idv2", t1);
  let t2;
  if ($[1] !== missingRequirements) {
    t2 = {
      missingRequirements,
      isContinuingOnDesktop: false,
      isAppClipProcessing: false
    };
    $[1] = missingRequirements;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const {
    title
  } = useRequirementsTitle(t2);
  let t3;
  if ($[3] !== authToken || $[4] !== device || $[5] !== l10n || $[6] !== onDone || $[7] !== sandboxIdDocOutcome) {
    t3 = {
      authToken,
      device,
      l10n,
      sandboxIdDocOutcome,
      onFinish: onDone
    };
    $[3] = authToken;
    $[4] = device;
    $[5] = l10n;
    $[6] = onDone;
    $[7] = sandboxIdDocOutcome;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const d2p = useD2pSession(t3);
  let t4;
  if ($[9] !== config || $[10] !== d2p.state.scopedAuthToken || $[11] !== fromContinueOnMobile || $[12] !== l10n.language || $[13] !== missingRequirements) {
    t4 = {
      authToken: d2p.state.scopedAuthToken,
      language: l10n.language,
      missingRequirements,
      onboardingConfig: config,
      fromContinueOnMobile
    };
    $[9] = config;
    $[10] = d2p.state.scopedAuthToken;
    $[11] = fromContinueOnMobile;
    $[12] = l10n.language;
    $[13] = missingRequirements;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const handoffUrl = useCreateHandoffUrl(t4);
  const isProcessing = d2p.state.status === "in_progress";
  let t5;
  if ($[15] !== d2p) {
    t5 = () => {
      d2p.cancel();
      d2p.regenerate();
      logInfo("Transfer cancelled");
    };
    $[15] = d2p;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  const handleCancel = t5;
  if (isProcessing) {
    let t6;
    if ($[17] !== t) {
      t6 = t(_temp);
      $[17] = t;
      $[18] = t6;
    } else {
      t6 = $[18];
    }
    let t7;
    if ($[19] !== d2p.state.isCanceling || $[20] !== handleCancel || $[21] !== t6 || $[22] !== title) {
      t7 = <ProcessingOverlay title={title} subtitle={t6} isCanceling={d2p.state.isCanceling} onCancel={handleCancel} />;
      $[19] = d2p.state.isCanceling;
      $[20] = handleCancel;
      $[21] = t6;
      $[22] = title;
      $[23] = t7;
    } else {
      t7 = $[23];
    }
    return t7;
  }
  let t6;
  if ($[24] !== t) {
    t6 = t(_temp2);
    $[24] = t;
    $[25] = t6;
  } else {
    t6 = $[25];
  }
  let t7;
  if ($[26] !== t) {
    t7 = t(_temp3);
    $[26] = t;
    $[27] = t7;
  } else {
    t7 = $[27];
  }
  let t8;
  if ($[28] !== d2p.state.scopedAuthToken || $[29] !== handoffUrl) {
    t8 = <CopyOrSmsSendLink authToken={d2p.state.scopedAuthToken} handoffUrl={handoffUrl} />;
    $[28] = d2p.state.scopedAuthToken;
    $[29] = handoffUrl;
    $[30] = t8;
  } else {
    t8 = $[30];
  }
  let t9;
  if ($[31] !== t6 || $[32] !== t7 || $[33] !== t8) {
    t9 = <PageShell title={t6} subtitle={t7}>{t8}</PageShell>;
    $[31] = t6;
    $[32] = t7;
    $[33] = t8;
    $[34] = t9;
  } else {
    t9 = $[34];
  }
  return t9;
};
export default SmsTransfer;
function _temp($) {
  return $.processingSubtitle;
}
function _temp2($_0) {
  return $_0.title;
}
function _temp3($_1) {
  return $_1.subtitle;
}